import { LitElement, html } from 'lit';
import {
  updateMacroStepEvent,
  updateMicroStepEvent,
  displayProgressBarEvent,
} from '@cof/refi-web-sdk/dist/utils/update-progress-bar';
import { RefiAnalytics } from '@cof/refi-web-sdk/dist/index.js';
import { TagKey } from '@cof/tag-catalog-fs-lienholder-mfe';
import { SessionService } from '@services/session.service';
import { DebugService } from '@services/debug.service';
import { AppService } from './app.service';
import { LenderService } from '@services/lender.service';
import { REFI_LIENHOLDER_CONST } from './shared/constants/app.constants';
import { stratum } from '../constants/stratum.js';
import { environment } from '../environment/environment.js';
import '@components/lender-checkout/lender-checkout.component';
import '@components/lender-info/lender-info.component';
import { ExperimentService } from '@services/experiment.service';
import { CookieService } from '@services/cookie.service';
import { customElement, property, state } from 'lit/decorators.js';
import { RefiSession } from '@models/refi-session.js';
import { Experiments } from '@models/experiment.model.js';
import {
  getIsV2Routing,
  setIsV2Routing,
} from '@services/routing-v2.service.ts';
import { HostAppEventsService } from '@services/host-app-events.service.ts';
import { ApiService } from '@services/api.service';
import { FeatureToggle } from '@models/feature-toggle.model';

@customElement('refi-lienholder-app')
export class RefiLienholderApp extends LitElement {
  analytics: RefiAnalytics;
  private debug: DebugService;

  @state()
  isTesting = false;

  @state()
  session: RefiSession;

  @state()
  checkout = false;

  @state()
  loadingApp = false;

  @property({
    type: Object,
    hasChanged: (newVal: Experiments, oldVal: Experiments) => {
      if (newVal !== oldVal) {
        // loop through this.experiments and add the object name to experimentIds array if it's eligible
        const experimentIds: string[] = [];
        Object.keys(newVal).forEach((experiment) => {
          if (newVal[experiment].isEligible) {
            experimentIds.push(newVal[experiment].allocation);
          } else {
            experimentIds.push(`${experiment}-ineligible`);
          }
        });
        ExperimentService.updateExperiments(experimentIds);
        return true;
      }
      return false;
    },
  })
  experiments: Experiments;

  @property({ type: String, attribute: 'checkout-page' })
  checkoutPage: string;

  @property({
    type: Boolean,
    attribute: 'v2-routing',
    reflect: true,
    hasChanged: (newVal: boolean) => {
      setIsV2Routing(newVal);
      return true;
    },
  })
  v2Routing: boolean;

  @property({ type: String, attribute: 'current-step', reflect: true })
  currentStep: string;

  @property({ type: Object, attribute: 'feature-toggles' })
  featureToggles: FeatureToggle[];

  constructor() {
    super();
    this.debug = new DebugService('RefiLienholderApp');
    if (document?.location) {
      this.isTesting =
        new URL(document.location.href)?.searchParams?.get('testing') ===
        'lighthouse';
    }
    this.updateStratumMarketingChannel();
    displayProgressBarEvent(true);
    updateMacroStepEvent('Lock in payment', ['Offers', 'Review', 'Contract']);
    updateMicroStepEvent('Contract');
    setTimeout(() => {
      updateMacroStepEvent('Verify information', ['Lienholder', 'Stips']);
    }, 30);
    this.addEventListener(REFI_LIENHOLDER_CONST.ROUTE_CHANGE, () => {
      this.requestUpdate();
    });
  }

  render() {
    this.debug.log('v2 routing:', getIsV2Routing());
    this.debug.log('rendered');

    if (this.checkout || (!environment.isProdEnv && this.isCheckoutHash())) {
      return html`
        <div role="main">
          <refi-lender-checkout></refi-lender-checkout>
        </div>
      `;
    }

    return html`
      <div role="main">
        ${this.currentStep === 'lender-info' ||
        window.location.href.endsWith('lender-info')
          ? html`<refi-lender-info></refi-lender-info>`
          : ''}
      </div>
      ${this.loadingApp
        ? html`
            <refi-loading-spinner
              message="Loading Lender Info Page"
              class="_gb-show"
            ></refi-loading-spinner>
          `
        : ''}
    `;
  }

  async firstUpdated() {
    this.loadingApp = true;
    if (this.isTesting && !environment.isProdEnv) {
      this.session = SessionService.mockSession;
    } else {
      this.session = SessionService.session || {};
    }
    const appId = this.session?.currentApp?.appId || '';
    stratum.setExternalVar('applicationId', appId.toString());
    this.analytics = new RefiAnalytics(
      'lienholder',
      ['sitecatalyst', 'potomac'],
      appId
    );
    if (this.isCheckoutPage()) {
      this.loadingApp = false;
      LenderService.appId = this.session.eAppId;
    } else {
      await this.updateComplete;
      LenderService.appId = this.session.eAppId;
      if (this.session.currentApp?.lenderInfo) {
        LenderService.lienholderModel = this.session.currentApp?.lenderInfo;
      } else if (this.session.eAppId) {
        // CWE-312: lienholder PII is no longer cached in sessionStorage, so
        // re-fetch it from the authoritative BFF (the same source
        // lender-checkout uses) before the lender-info form renders its prefill.
        await LenderService.retrieveLienholder();
      }
      AppService.updateRoute();
      stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_LIENHOLDER);
      if (this.session.eAppId) {
        this.loadingApp = false;
      }
    }

    HostAppEventsService.hideLoaderSpinnerEvent();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  updated(changes: Map<string, unknown>) {
    super.updated(changes);
    if (changes.has('featureToggles') && this.featureToggles?.length) {
      const bffPostappRouting = this.featureToggles.find(
        (t) =>
          t.featureKey ===
          REFI_LIENHOLDER_CONST.FEATURE_TOGGLES.BFF_POSTAPP_ROUTING
      );
      if (bffPostappRouting?.isEnabled) {
        ApiService.setUpstreamHeader('gen3');
      }
    }
  }

  updateStratumMarketingChannel() {
    const parsedCookie = CookieService.getCookie(
      REFI_LIENHOLDER_CONST.MARKETING_ATTRIBUTE_COOKIE
    )?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1]; // oldest non-bau attribution
    if (parsedCookie?.[0] === 'external_id') {
      // sets var to query param external id if first time with offer code, otherwise cookie
      stratum.setExternalVar(
        'marketingChannel',
        externalIdFromCookie ?? SessionService.getExternalId()!
      );
    }
  }

  isCheckoutPage() {
    return this.checkout || (!environment.isProdEnv && this.isCheckoutHash());
  }

  isCheckoutHash() {
    // return window.location.pathname.includes('checkout');
    return (
      window.location.hash &&
      window.location.hash.includes('#/nonprod/lienholder/checkout')
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'refi-lienholder-app': RefiLienholderApp;
  }
}
